/**
 * CDC 2000 BMI-for-age reference, as LMS parameters.
 *
 * GENERATED — do not hand-edit. Source: CDC's own `bmiagerev.csv`
 * (https://www.cdc.gov/growthcharts/data/zscore/bmiagerev.csv), trimmed to the
 * four columns the percentile maths needs and rounded to 6 significant digits.
 *
 * Why this file exists: a child's BMI means nothing against the adult WHO
 * cutoffs. A 5-year-old girl at BMI 14.9 sits near the 45th percentile — an
 * ordinary, healthy child — but the adult table calls anything under 18.5
 * "Underweight". Nurses were being shown a false clinical finding on every
 * pediatric visit. CDC BMI-for-age percentiles are the standard for ages 2
 * through 19; `classifyBmiForAge` in `bmi-classify.ts` reads this table.
 *
 * Each row is [ageMonths, L, M, S]. Rows run from 24 months to 240.5 months —
 * the full span CDC publishes. Below 24 months BMI-for-age is not defined
 * (weight-for-length is used instead), which is why we report no category for
 * an under-2, rather than guessing.
 *
 * L/M/S are the Box-Cox skewness, median and coefficient of variation. The
 * z-score for a measured BMI is
 *
 *     z = ((BMI / M) ^ L - 1) / (L * S)          for L != 0
 *     z = ln(BMI / M) / S                        for L == 0
 *
 * and the percentile is the standard normal CDF of z.
 */

/** One reference row: [ageMonths, L, M, S]. */
export type LmsRow = readonly [number, number, number, number];

export const CDC_BMI_LMS_MALE: readonly LmsRow[] = [
    [24, -2.01118, 16.575, 0.0805925],
    [24.5, -1.98237, 16.5478, 0.0801274],
    [25.5, -1.9241, 16.4944, 0.079234],
    [26.5, -1.8655, 16.4426, 0.0783894],
    [27.5, -1.80726, 16.3922, 0.0775935],
    [28.5, -1.75012, 16.3433, 0.0768465],
    [29.5, -1.69482, 16.2958, 0.0761483],
    [30.5, -1.64211, 16.2497, 0.0754991],
    [31.5, -1.59274, 16.205, 0.074899],
    [32.5, -1.54744, 16.1615, 0.074348],
    [33.5, -1.5069, 16.1193, 0.0738461],
    [34.5, -1.47177, 16.0784, 0.0733934],
    [35.5, -1.44263, 16.0388, 0.0729896],
    [36.5, -1.41999, 16.0003, 0.0726344],
    [37.5, -1.40428, 15.963, 0.0723276],
    [38.5, -1.39586, 15.927, 0.0720686],
    [39.5, -1.39494, 15.892, 0.0718568],
    [40.5, -1.40167, 15.8582, 0.0716913],
    [41.5, -1.4161, 15.8256, 0.0715711],
    [42.5, -1.43816, 15.7941, 0.0714951],
    [43.5, -1.46767, 15.7636, 0.0714621],
    [44.5, -1.50438, 15.7343, 0.0714706],
    [45.5, -1.54794, 15.7061, 0.0715192],
    [46.5, -1.5979, 15.679, 0.0716063],
    [47.5, -1.65373, 15.6531, 0.0717302],
    [48.5, -1.71487, 15.6282, 0.0718892],
    [49.5, -1.78067, 15.6044, 0.0720817],
    [50.5, -1.85047, 15.5818, 0.0723061],
    [51.5, -1.92355, 15.5603, 0.0725606],
    [52.5, -1.99922, 15.5399, 0.0728438],
    [53.5, -2.07671, 15.5206, 0.0731543],
    [54.5, -2.15535, 15.5026, 0.0734907],
    [55.5, -2.23444, 15.4857, 0.0738517],
    [56.5, -2.31332, 15.47, 0.0742362],
    [57.5, -2.39138, 15.4555, 0.0746434],
    [58.5, -2.46803, 15.4421, 0.0750723],
    [59.5, -2.54278, 15.43, 0.0755221],
    [60.5, -2.61517, 15.4191, 0.0759922],
    [61.5, -2.68479, 15.4095, 0.0764821],
    [62.5, -2.75132, 15.401, 0.0769912],
    [63.5, -2.81446, 15.3938, 0.0775191],
    [64.5, -2.87402, 15.3878, 0.0780654],
    [65.5, -2.92984, 15.3831, 0.0786296],
    [66.5, -2.9818, 15.3795, 0.0792114],
    [67.5, -3.02983, 15.3772, 0.0798103],
    [68.5, -3.07392, 15.3761, 0.0804261],
    [69.5, -3.11409, 15.3762, 0.0810582],
    [70.5, -3.15039, 15.3775, 0.0817062],
    [71.5, -3.18289, 15.3799, 0.0823697],
    [72.5, -3.21171, 15.3835, 0.0830482],
    [73.5, -3.23695, 15.3883, 0.083741],
    [74.5, -3.25876, 15.3942, 0.0844477],
    [75.5, -3.27728, 15.4013, 0.0851677],
    [76.5, -3.29268, 15.4094, 0.0859002],
    [77.5, -3.30512, 15.4187, 0.0866447],
    [78.5, -3.31477, 15.429, 0.0874004],
    [79.5, -3.32179, 15.4404, 0.0881667],
    [80.5, -3.32635, 15.4529, 0.0889429],
    [81.5, -3.3286, 15.4664, 0.0897282],
    [82.5, -3.32873, 15.4809, 0.0905219],
    [83.5, -3.32687, 15.4964, 0.0913232],
    [84.5, -3.32319, 15.5129, 0.0921313],
    [85.5, -3.31783, 15.5303, 0.0929455],
    [86.5, -3.31092, 15.5488, 0.0937651],
    [87.5, -3.30261, 15.5681, 0.0945893],
    [88.5, -3.29302, 15.5884, 0.0954172],
    [89.5, -3.28226, 15.6096, 0.0962483],
    [90.5, -3.27045, 15.6317, 0.0970817],
    [91.5, -3.2577, 15.6547, 0.0979167],
    [92.5, -3.24411, 15.6785, 0.0987526],
    [93.5, -3.22976, 15.7032, 0.0995887],
    [94.5, -3.21475, 15.7288, 0.100424],
    [95.5, -3.19916, 15.7551, 0.101259],
    [96.5, -3.18306, 15.7823, 0.102091],
    [97.5, -3.16652, 15.8103, 0.102921],
    [98.5, -3.14961, 15.839, 0.103748],
    [99.5, -3.13239, 15.8686, 0.104571],
    [100.5, -3.11491, 15.8989, 0.10539],
    [101.5, -3.09723, 15.9299, 0.106204],
    [102.5, -3.07938, 15.9617, 0.107013],
    [103.5, -3.06142, 15.9942, 0.107815],
    [104.5, -3.04339, 16.0274, 0.108611],
    [105.5, -3.02531, 16.0613, 0.1094],
    [106.5, -3.00723, 16.0959, 0.110182],
    [107.5, -2.98916, 16.1312, 0.110955],
    [108.5, -2.97115, 16.1671, 0.111721],
    [109.5, -2.95321, 16.2037, 0.112477],
    [110.5, -2.93536, 16.2409, 0.113224],
    [111.5, -2.91764, 16.2788, 0.113962],
    [112.5, -2.90004, 16.3173, 0.114689],
    [113.5, -2.88259, 16.3564, 0.115407],
    [114.5, -2.86531, 16.3961, 0.116113],
    [115.5, -2.8482, 16.4363, 0.116809],
    [116.5, -2.83129, 16.4772, 0.117493],
    [117.5, -2.81456, 16.5186, 0.118166],
    [118.5, -2.79804, 16.5606, 0.118827],
    [119.5, -2.78174, 16.6031, 0.119476],
    [120.5, -2.76565, 16.6461, 0.120112],
    [121.5, -2.74978, 16.6897, 0.120737],
    [122.5, -2.73414, 16.7338, 0.121348],
    [123.5, -2.71873, 16.7784, 0.121947],
    [124.5, -2.70356, 16.8235, 0.122533],
    [125.5, -2.68861, 16.8691, 0.123105],
    [126.5, -2.6739, 16.9151, 0.123664],
    [127.5, -2.65943, 16.9616, 0.12421],
    [128.5, -2.64519, 17.0086, 0.124742],
    [129.5, -2.63119, 17.056, 0.125261],
    [130.5, -2.61741, 17.1039, 0.125766],
    [131.5, -2.60387, 17.1522, 0.126257],
    [132.5, -2.59056, 17.2009, 0.126735],
    [133.5, -2.57747, 17.25, 0.127198],
    [134.5, -2.56461, 17.2995, 0.127648],
    [135.5, -2.55197, 17.3494, 0.128084],
    [136.5, -2.53954, 17.3997, 0.128506],
    [137.5, -2.52733, 17.4504, 0.128914],
    [138.5, -2.51532, 17.5014, 0.129309],
    [139.5, -2.50352, 17.5528, 0.12969],
    [140.5, -2.49192, 17.6045, 0.130057],
    [141.5, -2.48051, 17.6565, 0.13041],
    [142.5, -2.4693, 17.7089, 0.13075],
    [143.5, -2.45827, 17.7616, 0.131076],
    [144.5, -2.44743, 17.8146, 0.131389],
    [145.5, -2.43676, 17.8679, 0.131689],
    [146.5, -2.42626, 17.9216, 0.131975],
    [147.5, -2.41592, 17.9754, 0.132248],
    [148.5, -2.40575, 18.0296, 0.132508],
    [149.5, -2.39573, 18.084, 0.132756],
    [150.5, -2.38586, 18.1387, 0.132991],
    [151.5, -2.37613, 18.1937, 0.133213],
    [152.5, -2.36654, 18.2488, 0.133423],
    [153.5, -2.35709, 18.3043, 0.13362],
    [154.5, -2.34776, 18.3599, 0.133806],
    [155.5, -2.33855, 18.4157, 0.133979],
    [156.5, -2.32946, 18.4718, 0.134141],
    [157.5, -2.32047, 18.5281, 0.134292],
    [158.5, -2.3116, 18.5845, 0.134431],
    [159.5, -2.30282, 18.6411, 0.134559],
    [160.5, -2.29413, 18.6979, 0.134677],
    [161.5, -2.28553, 18.7549, 0.134783],
    [162.5, -2.27702, 18.812, 0.13488],
    [163.5, -2.26858, 18.8693, 0.134966],
    [164.5, -2.26021, 18.9267, 0.135042],
    [165.5, -2.25191, 18.9842, 0.135108],
    [166.5, -2.24367, 19.0419, 0.135165],
    [167.5, -2.23549, 19.0997, 0.135212],
    [168.5, -2.22736, 19.1576, 0.135251],
    [169.5, -2.21928, 19.2156, 0.135281],
    [170.5, -2.21124, 19.2737, 0.135302],
    [171.5, -2.20324, 19.3318, 0.135316],
    [172.5, -2.19527, 19.3901, 0.135321],
    [173.5, -2.18734, 19.4484, 0.135318],
    [174.5, -2.17943, 19.5067, 0.135309],
    [175.5, -2.17154, 19.5651, 0.135292],
    [176.5, -2.16367, 19.6236, 0.135268],
    [177.5, -2.15582, 19.6821, 0.135238],
    [178.5, -2.14799, 19.7406, 0.135201],
    [179.5, -2.14016, 19.7991, 0.135158],
    [180.5, -2.13234, 19.8577, 0.13511],
    [181.5, -2.12454, 19.9162, 0.135057],
    [182.5, -2.11674, 19.9747, 0.134998],
    [183.5, -2.10894, 20.0332, 0.134934],
    [184.5, -2.10115, 20.0917, 0.134866],
    [185.5, -2.09336, 20.1502, 0.134794],
    [186.5, -2.08557, 20.2086, 0.134718],
    [187.5, -2.07779, 20.2669, 0.134638],
    [188.5, -2.07002, 20.3252, 0.134556],
    [189.5, -2.06225, 20.3835, 0.13447],
    [190.5, -2.0545, 20.4416, 0.134382],
    [191.5, -2.04675, 20.4997, 0.134291],
    [192.5, -2.03902, 20.5576, 0.134198],
    [193.5, -2.0313, 20.6155, 0.134104],
    [194.5, -2.02361, 20.6733, 0.134009],
    [195.5, -2.01594, 20.7309, 0.133912],
    [196.5, -2.00831, 20.7884, 0.133815],
    [197.5, -2.00071, 20.8457, 0.133718],
    [198.5, -1.99315, 20.9029, 0.13362],
    [199.5, -1.98564, 20.96, 0.133523],
    [200.5, -1.97819, 21.0169, 0.133427],
    [201.5, -1.97081, 21.0736, 0.133332],
    [202.5, -1.9635, 21.1301, 0.133238],
    [203.5, -1.95627, 21.1864, 0.133146],
    [204.5, -1.94913, 21.2425, 0.133057],
    [205.5, -1.9421, 21.2984, 0.13297],
    [206.5, -1.93518, 21.354, 0.132885],
    [207.5, -1.92838, 21.4094, 0.132804],
    [208.5, -1.92171, 21.4646, 0.132727],
    [209.5, -1.91519, 21.5195, 0.132654],
    [210.5, -1.90883, 21.5742, 0.132585],
    [211.5, -1.90264, 21.6285, 0.132521],
    [212.5, -1.89663, 21.6826, 0.132462],
    [213.5, -1.89082, 21.7364, 0.132409],
    [214.5, -1.88521, 21.7899, 0.132361],
    [215.5, -1.87982, 21.843, 0.13232],
    [216.5, -1.87467, 21.8959, 0.132286],
    [217.5, -1.86976, 21.9484, 0.13226],
    [218.5, -1.86511, 22.0005, 0.13224],
    [219.5, -1.86073, 22.0523, 0.132229],
    [220.5, -1.85663, 22.1037, 0.132227],
    [221.5, -1.85283, 22.1548, 0.132233],
    [222.5, -1.84932, 22.2054, 0.132249],
    [223.5, -1.84613, 22.2557, 0.132275],
    [224.5, -1.84326, 22.3055, 0.132311],
    [225.5, -1.84072, 22.355, 0.132357],
    [226.5, -1.83852, 22.404, 0.132415],
    [227.5, -1.83666, 22.4526, 0.132485],
    [228.5, -1.83514, 22.5007, 0.132566],
    [229.5, -1.83397, 22.5484, 0.132661],
    [230.5, -1.83316, 22.5957, 0.132768],
    [231.5, -1.8327, 22.6424, 0.132889],
    [232.5, -1.83258, 22.6887, 0.133024],
    [233.5, -1.83282, 22.7346, 0.133174],
    [234.5, -1.8334, 22.7799, 0.133339],
    [235.5, -1.83432, 22.8247, 0.133519],
    [236.5, -1.83556, 22.8691, 0.133716],
    [237.5, -1.83712, 22.9129, 0.13393],
    [238.5, -1.83899, 22.9563, 0.13416],
    [239.5, -1.84115, 22.9991, 0.134408],
    [240, -1.84233, 23.0203, 0.134539],
    [240.5, -1.84358, 23.0414, 0.134675]
];

export const CDC_BMI_LMS_FEMALE: readonly LmsRow[] = [
    [24, -0.986609, 16.4234, 0.0854518],
    [24.5, -1.0245, 16.388, 0.0850258],
    [25.5, -1.1027, 16.319, 0.0842141],
    [26.5, -1.18397, 16.2521, 0.0834551],
    [27.5, -1.26807, 16.1873, 0.0827483],
    [28.5, -1.35475, 16.1248, 0.0820927],
    [29.5, -1.44369, 16.0643, 0.0814877],
    [30.5, -1.53454, 16.0059, 0.0809324],
    [31.5, -1.62693, 15.9497, 0.0804262],
    [32.5, -1.72043, 15.8955, 0.0799682],
    [33.5, -1.81464, 15.8434, 0.0795577],
    [34.5, -1.90908, 15.7933, 0.0791942],
    [35.5, -2.0033, 15.7453, 0.0788769],
    [36.5, -2.09683, 15.6992, 0.0786053],
    [37.5, -2.18921, 15.6552, 0.0783787],
    [38.5, -2.27999, 15.6132, 0.0781967],
    [39.5, -2.36873, 15.5732, 0.0780587],
    [40.5, -2.45502, 15.5351, 0.0779642],
    [41.5, -2.53847, 15.4989, 0.0779127],
    [42.5, -2.61873, 15.4647, 0.0779037],
    [43.5, -2.69549, 15.4324, 0.0779368],
    [44.5, -2.76846, 15.4019, 0.0780113],
    [45.5, -2.83743, 15.3734, 0.0781268],
    [46.5, -2.90218, 15.3466, 0.0782827],
    [47.5, -2.96258, 15.3217, 0.0784784],
    [48.5, -3.01852, 15.2985, 0.0787133],
    [49.5, -3.06994, 15.2772, 0.0789867],
    [50.5, -3.1168, 15.2576, 0.0792978],
    [51.5, -3.15911, 15.2397, 0.079646],
    [52.5, -3.19691, 15.2235, 0.0800304],
    [53.5, -3.23028, 15.2089, 0.0804501],
    [54.5, -3.2593, 15.1961, 0.0809044],
    [55.5, -3.2841, 15.1848, 0.0813922],
    [56.5, -3.30481, 15.1751, 0.0819126],
    [57.5, -3.3216, 15.167, 0.0824647],
    [58.5, -3.33462, 15.1605, 0.0830473],
    [59.5, -3.34405, 15.1554, 0.0836595],
    [60.5, -3.35008, 15.1519, 0.0843001],
    [61.5, -3.35289, 15.1498, 0.0849682],
    [62.5, -3.35269, 15.1492, 0.0856625],
    [63.5, -3.34966, 15.1499, 0.086382],
    [64.5, -3.344, 15.1521, 0.0871256],
    [65.5, -3.33589, 15.1557, 0.087892],
    [66.5, -3.32552, 15.1606, 0.0886803],
    [67.5, -3.31308, 15.1668, 0.0894891],
    [68.5, -3.29873, 15.1743, 0.0903174],
    [69.5, -3.28265, 15.1831, 0.0911641],
    [70.5, -3.265, 15.1931, 0.092028],
    [71.5, -3.24594, 15.2044, 0.092908],
    [72.5, -3.22561, 15.2169, 0.093803],
    [73.5, -3.20415, 15.2306, 0.0947119],
    [74.5, -3.18169, 15.2454, 0.0956336],
    [75.5, -3.15836, 15.2614, 0.096567],
    [76.5, -3.13428, 15.2785, 0.097511],
    [77.5, -3.10956, 15.2968, 0.0984647],
    [78.5, -3.08429, 15.3161, 0.099427],
    [79.5, -3.05858, 15.3364, 0.100397],
    [80.5, -3.03251, 15.3579, 0.101373],
    [81.5, -3.00616, 15.3803, 0.102355],
    [82.5, -2.97961, 15.4037, 0.103342],
    [83.5, -2.95293, 15.4282, 0.104332],
    [84.5, -2.92619, 15.4536, 0.105325],
    [85.5, -2.89944, 15.4799, 0.10632],
    [86.5, -2.87273, 15.5072, 0.107316],
    [87.5, -2.84612, 15.5354, 0.108313],
    [88.5, -2.81966, 15.5644, 0.109308],
    [89.5, -2.79337, 15.5944, 0.110303],
    [90.5, -2.76731, 15.6252, 0.111295],
    [91.5, -2.7415, 15.6568, 0.112284],
    [92.5, -2.71597, 15.6893, 0.113269],
    [93.5, -2.69075, 15.7226, 0.11425],
    [94.5, -2.66587, 15.7566, 0.115225],
    [95.5, -2.64134, 15.7914, 0.116195],
    [96.5, -2.61719, 15.827, 0.117159],
    [97.5, -2.59343, 15.8633, 0.118115],
    [98.5, -2.57008, 15.9003, 0.119064],
    [99.5, -2.54714, 15.938, 0.120004],
    [100.5, -2.52464, 15.9764, 0.120936],
    [101.5, -2.50257, 16.0155, 0.121858],
    [102.5, -2.48095, 16.0552, 0.122771],
    [103.5, -2.45979, 16.0955, 0.123673],
    [104.5, -2.43908, 16.1365, 0.124564],
    [105.5, -2.41884, 16.178, 0.125445],
    [106.5, -2.39906, 16.2201, 0.126313],
    [107.5, -2.37976, 16.2628, 0.12717],
    [108.5, -2.36092, 16.3061, 0.128014],
    [109.5, -2.34256, 16.3499, 0.128845],
    [110.5, -2.32466, 16.3942, 0.129663],
    [111.5, -2.30724, 16.439, 0.130467],
    [112.5, -2.29029, 16.4843, 0.131258],
    [113.5, -2.2738, 16.53, 0.132034],
    [114.5, -2.25778, 16.5763, 0.132797],
    [115.5, -2.24223, 16.6229, 0.133545],
    [116.5, -2.22713, 16.67, 0.134277],
    [117.5, -2.2125, 16.7175, 0.134995],
    [118.5, -2.19831, 16.7654, 0.135698],
    [119.5, -2.18458, 16.8137, 0.136385],
    [120.5, -2.1713, 16.8623, 0.137057],
    [121.5, -2.15845, 16.9113, 0.137713],
    [122.5, -2.14605, 16.9606, 0.138353],
    [123.5, -2.13408, 17.0103, 0.138978],
    [124.5, -2.12255, 17.0602, 0.139586],
    [125.5, -2.11144, 17.1105, 0.140178],
    [126.5, -2.10075, 17.161, 0.140754],
    [127.5, -2.09048, 17.2117, 0.141314],
    [128.5, -2.08062, 17.2628, 0.141857],
    [129.5, -2.07117, 17.314, 0.142384],
    [130.5, -2.06213, 17.3655, 0.142895],
    [131.5, -2.05348, 17.4172, 0.14339],
    [132.5, -2.04524, 17.4691, 0.143868],
    [133.5, -2.03738, 17.5211, 0.14433],
    [134.5, -2.02991, 17.5733, 0.144776],
    [135.5, -2.02282, 17.6257, 0.145206],
    [136.5, -2.01611, 17.6782, 0.14562],
    [137.5, -2.00977, 17.7308, 0.146017],
    [138.5, -2.0038, 17.7836, 0.146399],
    [139.5, -1.9982, 17.8364, 0.146765],
    [140.5, -1.99296, 17.8893, 0.147115],
    [141.5, -1.98807, 17.9423, 0.14745],
    [142.5, -1.98354, 17.9953, 0.147769],
    [143.5, -1.97936, 18.0484, 0.148073],
    [144.5, -1.97552, 18.1015, 0.148361],
    [145.5, -1.97202, 18.1546, 0.148635],
    [146.5, -1.96886, 18.2077, 0.148894],
    [147.5, -1.96604, 18.2609, 0.149138],
    [148.5, -1.96354, 18.3139, 0.149367],
    [149.5, -1.96137, 18.367, 0.149582],
    [150.5, -1.95952, 18.42, 0.149783],
    [151.5, -1.95799, 18.473, 0.149971],
    [152.5, -1.95677, 18.5259, 0.150144],
    [153.5, -1.95587, 18.5787, 0.150304],
    [154.5, -1.95527, 18.6314, 0.150451],
    [155.5, -1.95497, 18.684, 0.150584],
    [156.5, -1.95498, 18.7364, 0.150705],
    [157.5, -1.95528, 18.7888, 0.150813],
    [158.5, -1.95587, 18.841, 0.15091],
    [159.5, -1.95676, 18.893, 0.150994],
    [160.5, -1.95792, 18.9449, 0.151066],
    [161.5, -1.95937, 18.9966, 0.151127],
    [162.5, -1.9611, 19.0481, 0.151176],
    [163.5, -1.9631, 19.0994, 0.151215],
    [164.5, -1.96537, 19.1505, 0.151243],
    [165.5, -1.96791, 19.2014, 0.151261],
    [166.5, -1.97071, 19.252, 0.151269],
    [167.5, -1.97376, 19.3024, 0.151267],
    [168.5, -1.97707, 19.3526, 0.151256],
    [169.5, -1.98063, 19.4024, 0.151235],
    [170.5, -1.98444, 19.452, 0.151206],
    [171.5, -1.98848, 19.5014, 0.151169],
    [172.5, -1.99276, 19.5504, 0.151123],
    [173.5, -1.99728, 19.5991, 0.15107],
    [174.5, -2.00201, 19.6475, 0.15101],
    [175.5, -2.00697, 19.6955, 0.150942],
    [176.5, -2.01215, 19.7432, 0.150868],
    [177.5, -2.01753, 19.7906, 0.150787],
    [178.5, -2.02312, 19.8376, 0.150701],
    [179.5, -2.02891, 19.8843, 0.150609],
    [180.5, -2.03489, 19.9306, 0.150512],
    [181.5, -2.04106, 19.9765, 0.15041],
    [182.5, -2.04741, 20.022, 0.150303],
    [183.5, -2.05393, 20.067, 0.150193],
    [184.5, -2.06062, 20.1117, 0.150079],
    [185.5, -2.06746, 20.156, 0.149962],
    [186.5, -2.07446, 20.1998, 0.149843],
    [187.5, -2.0816, 20.2432, 0.14972],
    [188.5, -2.08888, 20.2861, 0.149596],
    [189.5, -2.09628, 20.3286, 0.149471],
    [190.5, -2.1038, 20.3706, 0.149344],
    [191.5, -2.11143, 20.4122, 0.149217],
    [192.5, -2.11916, 20.4533, 0.14909],
    [193.5, -2.12698, 20.4938, 0.148963],
    [194.5, -2.13487, 20.5339, 0.148837],
    [195.5, -2.14284, 20.5735, 0.148712],
    [196.5, -2.15087, 20.6126, 0.148589],
    [197.5, -2.15894, 20.6511, 0.148468],
    [198.5, -2.16704, 20.6891, 0.148349],
    [199.5, -2.17518, 20.7266, 0.148234],
    [200.5, -2.18332, 20.7636, 0.148123],
    [201.5, -2.19146, 20.7999, 0.148015],
    [202.5, -2.19958, 20.8358, 0.147913],
    [203.5, -2.20768, 20.8711, 0.147815],
    [204.5, -2.21574, 20.9058, 0.147723],
    [205.5, -2.22374, 20.9399, 0.147638],
    [206.5, -2.23167, 20.9734, 0.147559],
    [207.5, -2.23951, 21.0064, 0.147488],
    [208.5, -2.24726, 21.0387, 0.147424],
    [209.5, -2.25489, 21.0705, 0.147369],
    [210.5, -2.26238, 21.1016, 0.147323],
    [211.5, -2.26973, 21.1322, 0.147287],
    [212.5, -2.27692, 21.1621, 0.14726],
    [213.5, -2.28393, 21.1913, 0.147245],
    [214.5, -2.29073, 21.22, 0.147241],
    [215.5, -2.29732, 21.248, 0.147248],
    [216.5, -2.30369, 21.2753, 0.147269],
    [217.5, -2.3098, 21.302, 0.147302],
    [218.5, -2.31565, 21.3281, 0.14735],
    [219.5, -2.32122, 21.3534, 0.147411],
    [220.5, -2.32648, 21.3781, 0.147488],
    [221.5, -2.33143, 21.4021, 0.14758],
    [222.5, -2.33604, 21.4255, 0.147689],
    [223.5, -2.3403, 21.4481, 0.147815],
    [224.5, -2.34418, 21.4701, 0.147959],
    [225.5, -2.34768, 21.4913, 0.148121],
    [226.5, -2.35077, 21.5119, 0.148302],
    [227.5, -2.35344, 21.5317, 0.148502],
    [228.5, -2.35568, 21.5508, 0.148724],
    [229.5, -2.35746, 21.5692, 0.148966],
    [230.5, -2.35876, 21.5869, 0.14923],
    [231.5, -2.35959, 21.6038, 0.149517],
    [232.5, -2.35991, 21.62, 0.149827],
    [233.5, -2.35971, 21.6354, 0.150161],
    [234.5, -2.35898, 21.6501, 0.150521],
    [235.5, -2.35771, 21.664, 0.150905],
    [236.5, -2.35589, 21.6771, 0.151317],
    [237.5, -2.3535, 21.6895, 0.151755],
    [238.5, -2.35053, 21.7011, 0.152221],
    [239.5, -2.34696, 21.7119, 0.152716],
    [240, -2.34496, 21.717, 0.152975],
    [240.5, -2.3428, 21.7219, 0.153241]
];

/**
 * Plausible weight and height bands per year of age, ages 0 through 19.
 *
 * GENERATED alongside the LMS table above, from CDC's `wtageinf` / `wtage`
 * (weight-for-age) and `lenageinf` / `statage` (length- and stature-for-age)
 * z-score files. Each row is
 *
 *     [ageYears, weightLowLbs, weightHighLbs, heightLowIn, heightHighIn]
 *
 * where low/high are the 3rd and 97th percentiles across both sexes for any
 * month inside that year, converted from kg and cm.
 *
 * These drive the ADVISORY "please confirm" band only. A child outside them is
 * a child worth a second look, not a blocked form — see `checkMetricRange`.
 * The adult bands in `ADULT_METRIC_RANGES` are the numbers Q87 always used.
 */
export type AgeBandRow = readonly [number, number, number, number, number];

export const CDC_AGE_BANDS: readonly AgeBandRow[] = [
    [0, 5.2, 27.7, 17.7, 31.8],
    [1, 17.5, 34.2, 27.1, 36.8],
    [2, 22.0, 39.3, 30.9, 40.6],
    [3, 25.0, 46.3, 34.2, 43.3],
    [4, 28.2, 54.3, 36.6, 46.2],
    [5, 31.6, 63.0, 39.1, 49.1],
    [6, 35.3, 72.7, 41.6, 52.0],
    [7, 39.1, 83.9, 44.0, 54.7],
    [8, 43.1, 97.1, 46.2, 57.1],
    [9, 47.6, 112.1, 48.0, 59.5],
    [10, 52.9, 128.1, 49.6, 62.0],
    [11, 58.6, 144.0, 51.5, 64.8],
    [12, 65.0, 158.5, 53.4, 67.2],
    [13, 72.7, 170.4, 55.8, 70.2],
    [14, 80.9, 182.4, 58.3, 72.4],
    [15, 87.3, 195.1, 59.0, 73.6],
    [16, 92.2, 206.0, 59.2, 74.2],
    [17, 95.5, 213.9, 59.4, 74.6],
    [18, 97.5, 218.4, 59.4, 74.8],
    [19, 98.8, 221.9, 59.5, 74.9]
];
